import Parse from 'parse';

import { Injectable } from '@angular/core';

import {
  AnalysisStateTopology,
  ParseBaseRepository,
  resolveAnalystFromAnalysisState,
  SampleTopology,
} from '@telespot/infrastructure/parse';
import { Analyst, IState } from '@analysis/domain';
import { type AnalystRepository, FetchAnalystsOptions, scopeAnalystsToOwn } from '@analysis/use-cases';
import { ApiAuthErrors } from '@telespot/web-core';

@Injectable({
  providedIn: 'root',
})
export class TelespotAnalysisService extends ParseBaseRepository implements AnalystRepository {
  private readonly limit = 20;

  constructor() {
    super(Parse, { useMasterKey: false });
  }

  public async fetchForSample(sampleId: string, options?: FetchAnalystsOptions) {
    if (!options) {
      return [];
    }

    const results: Analyst[] = [];
    results.push(...(await this.fetchUserAnalysts(sampleId, options)));

    if (options.canUseAI) {
      results.push(...(await this.fetchAIAnalysts(sampleId)));
    }

    return results;
  }

  public async fetchUserAnalysts(sampleId: string, options: FetchAnalystsOptions): Promise<Analyst[]> {
    const Sample = Parse.Object.extend(SampleTopology.TABLE);
    const sampleRef = Sample.createWithoutData(sampleId);

    try {
      let userQuery = new Parse.Query(AnalysisStateTopology.TABLE)
        .equalTo(AnalysisStateTopology.SAMPLE, sampleRef)
        .exists(AnalysisStateTopology.USER)
        .containedIn(AnalysisStateTopology.STATE, [IState.INPROGRES, IState.ANALYZED])
        .include(AnalysisStateTopology.USER);

      // Each role is scoped to its own analyses only when its review feature is
      // off; otherwise it sees (and can review) everyone's. See scopeAnalystsToOwn.
      if (scopeAnalystsToOwn(options)) {
        const userRef = Parse.User.createWithoutData(options.currentUserId);
        userQuery = userQuery.equalTo(AnalysisStateTopology.USER, userRef);
      }

      //Rest of cases user can view everyone's analysis
      const results: Analyst[] = [];
      const userPages = this.getTokenPaginationGenerator({ query: userQuery, limit: this.limit });
      for await (const items of userPages) {
        results.push(
          ...items.map((as) => this.toAnalystItem(as)).filter((analyst): analyst is Analyst => analyst !== null)
        );
      }

      return results;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  public async fetchAIAnalysts(sampleId: string): Promise<Analyst[]> {
    const Sample = Parse.Object.extend(SampleTopology.TABLE);
    const sampleRef = Sample.createWithoutData(sampleId);

    try {
      const aiAnalystsQuery = new Parse.Query(AnalysisStateTopology.TABLE)
        .equalTo(AnalysisStateTopology.SAMPLE, sampleRef)
        .exists(AnalysisStateTopology.ALGORITHM)
        .include(AnalysisStateTopology.ALGORITHM);

      const results: Analyst[] = [];
      const algorithmPages = this.getTokenPaginationGenerator({
        query: aiAnalystsQuery,
        limit: this.limit,
      });
      for await (const items of algorithmPages) {
        results.push(
          ...items.map((as) => this.toAnalystItem(as)).filter((analyst): analyst is Analyst => analyst !== null)
        );
      }

      return results;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  public async getHistory(historyId: string) {
    try {
      const state = await new Parse.Query(AnalysisStateTopology.TABLE)
        .include(AnalysisStateTopology.USER, AnalysisStateTopology.ALGORITHM)
        .get(historyId);

      if (!state) return;

      return this.toAnalystItem(state) ?? undefined;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  public async getUserState(userId: string, sampleId: string) {
    const userRef = Parse.User.createWithoutData(userId);
    const sampleRef = Parse.Object.extend(SampleTopology.TABLE).createWithoutData(sampleId);

    try {
      const state = await new Parse.Query(AnalysisStateTopology.TABLE)
        .include(AnalysisStateTopology.USER, AnalysisStateTopology.ALGORITHM)
        .equalTo(AnalysisStateTopology.USER, userRef)
        .equalTo(AnalysisStateTopology.SAMPLE, sampleRef)
        .first();

      if (!state) return;

      return this.toAnalystItem(state) ?? undefined;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  private toAnalystItem(obj: Parse.Object): Analyst | null {
    const analyst = resolveAnalystFromAnalysisState(obj);
    if (!analyst) return null;

    return {
      name: analyst.name,
      historyId: obj.id,
      type: analyst.entity,
      id: analyst.id,
    };
  }
}
